import moment from "moment";

import { Customer } from "@typings/Customer";
import { InputField } from "@typings/InputField";
import {
  DEFAULT_LICENCE_VERSION,
  Licence,
  LicenceInput,
  LICENCE_VERSIONS,
  LICENCE_VERSION_LABELS,
  normalizeLicenceVersion,
} from "@typings/Licence";
import {
  getAllowedLicenceVersions,
  LicensingPolicy,
} from "@typings/LicensingPolicy";

export const TAG_TRIALS = ["PoC", "Trial"];

const getStartDateDefault = () =>
  moment.utc(moment(new Date()).format("YYYY-MM-DD")).toDate();
const getEndDateDefault = () =>
  moment.utc(moment(new Date()).format("YYYY-MM-DD")).add(1, "month").toDate();
const getYearEndDateDefault = () =>
  moment.utc(moment(new Date()).format("YYYY-MM-DD")).add(1, "year").toDate();

class InputFields {
  handleTagTrialTypeChange(inputFields: InputField[], trialSelected: boolean) {
    type PocTuple = [InputField, any];
    const replicasField: PocTuple = [
      inputFields.find(f => f.name === "replicas")!,
      5,
    ];
    const migrationsField: PocTuple = [
      inputFields.find(f => f.name === "migrations")!,
      5,
    ];
    const typeField: PocTuple = [
      inputFields.find(f => f.name === "type")!,
      false,
    ];
    const startDateField: PocTuple = [
      inputFields.find(f => f.name === "start_date")!,
      getStartDateDefault(),
    ];
    const endDateField: PocTuple = [
      inputFields.find(f => f.name === "end_date")!,
      getEndDateDefault(),
    ];

    const disableFields = (disabled: boolean) => {
      [
        replicasField,
        migrationsField,
        typeField,
        startDateField,
        endDateField,
      ].forEach(f => {
        f[0].disabled = disabled;
        if (disabled) {
          f[0].value = f[1];
        }
      });
    };

    if (trialSelected) {
      disableFields(true);
    } else {
      disableFields(false);
    }
  }

  resetQuantityDefaults(inputFields: InputField[]) {
    const replicasField = inputFields.find(f => f.name === "replicas");
    const migrationsField = inputFields.find(f => f.name === "migrations");
    if (replicasField && !replicasField.disabled) {
      replicasField.value = 5;
    }
    if (migrationsField && !migrationsField.disabled) {
      migrationsField.value = 5;
    }
  }

  applyTagEndDate(inputFields: InputField[], tag: string) {
    const YEAR_END_DATE_TAGS = ["Production", "NFR"];
    if (!YEAR_END_DATE_TAGS.includes(tag)) {
      return;
    }
    const endDateField = inputFields.find(f => f.name === "end_date");
    if (endDateField && !endDateField.disabled) {
      endDateField.value = getYearEndDateDefault();
    }
  }

  applyPolicyLicenceVersions(
    inputFields: InputField[],
    policy: LicensingPolicy | null,
  ) {
    const versionField = inputFields.find(f => f.name === "version");
    if (!versionField) {
      return;
    }
    const allowedVersions = getAllowedLicenceVersions(policy);
    versionField.enum = allowedVersions.map(version => ({
      label: LICENCE_VERSION_LABELS[version],
      value: version,
    }));
    versionField.hidden = allowedVersions.length < 2;
    if (!allowedVersions.includes(versionField.value)) {
      versionField.value = DEFAULT_LICENCE_VERSION;
    }
  }

  applyPolicyTagDefaults(
    inputFields: InputField[],
    tag: string,
    policy: LicensingPolicy | null,
  ) {
    if (!policy?.tag_policies?.[tag]) {
      return;
    }
    const tagPolicy = policy.tag_policies[tag];
    if (!tagPolicy.defaults) {
      return;
    }
    const replicasField = inputFields.find(f => f.name === "replicas");
    const migrationsField = inputFields.find(f => f.name === "migrations");

    if (replicasField && tagPolicy.defaults.replicas != null) {
      replicasField.value = tagPolicy.defaults.replicas;
      replicasField.disabled = tagPolicy.enforced === true;
    }
    if (migrationsField && tagPolicy.defaults.migrations != null) {
      migrationsField.value = tagPolicy.defaults.migrations;
      migrationsField.disabled = tagPolicy.enforced === true;
    }
  }

  FIELDS: InputField[] = [
    {
      name: "customer_id",
      type: "string",
      label: "Customer",
      enum: [],
      noItemsMessage: "Loading customers...",
      noSelectionMessage: "Select an customer",
      required: true,
      validate: (field: InputField) => {
        if (field.value === "__addnew__") {
          return Boolean(field.enumNewValue);
        }
        return Boolean(field.value);
      },
    },
    {
      name: "customer_email",
      type: "string",
      label: "Email",
      validate: (field: InputField) =>
        field.value
          ? /^\S.*@.*\..*\S$/.test(field.value) || "Incorrect email format"
          : true,
    },
    {
      name: "tag",
      type: "string",
      label: "Tag",
      enum: [],
      required: true,
      noItemsMessage: "Loading tags...",
      noSelectionMessage: "Select a tag",
      noAutocomplete: true,
    },
    {
      name: "installation_id",
      type: "string",
      label: "Installation ID",
      required: true,
    },
    {
      name: "description",
      type: "string",
      label: "Description",
    },
    {
      name: "replicas",
      type: "integer",
      label: "Number of VM Replicas",
      required: true,
      value: 5,
      validate: (field: InputField, fields: InputField[]) => {
        const migrationsField = fields.find(f => f.name === "migrations");
        return (
          Number(field.value) > 0 ||
          Boolean(migrationsField && Number(migrationsField.value) > 0)
        );
      },
    },
    {
      name: "migrations",
      type: "integer",
      label: "Number of VM Migrations",
      validate: (field: InputField, fields: InputField[]) => {
        const replicasField = fields.find(f => f.name === "replicas");
        return (
          Number(field.value) > 0 ||
          Boolean(replicasField && Number(replicasField.value) > 0)
        );
      },
      required: true,
      value: 5,
    },
    {
      name: "version",
      type: "string",
      label: "Licence Edition",
      required: true,
      value: DEFAULT_LICENCE_VERSION,
      enum: LICENCE_VERSIONS.map(version => ({
        label: LICENCE_VERSION_LABELS[version],
        value: version,
      })),
    },
    {
      name: "type",
      type: "boolean",
      label: "Licence Type",
      value: false,
    },
    {
      name: "start_date",
      type: "date",
      label: "Licence Start Date",
      value: getStartDateDefault(),
    },
    {
      name: "duration",
      type: "integer",
      label: "Licence Duration",
      value: 30,
      validate: (field: InputField) => {
        return Number(field.value) > 0;
      },
    },
    {
      name: "support",
      type: "string",
      label: "Coriolis Support",
      enum: [
        { label: "Complimentary", value: "Complimentary" },
        { label: "8x5 Support", value: "8x5 Support" },
        { label: "24x7 Support", value: "24x7 Support" },
      ],
      noSelectionMessage: "Select support type",
      hidden: true,
    },
    {
      name: "end_date",
      type: "date",
      label: "Licence End Date",
      value: getEndDateDefault(),
      validate: (field: InputField, fields: InputField[]) => {
        const startDate = fields.find(f => f.name === "start_date");
        if (!startDate) {
          return false;
        }
        const startDateValue = startDate.value;
        if (!startDateValue) {
          return false;
        }
        const licenceType = fields.find(f => f.name === "type");
        if (!field.value) {
          return false;
        }
        if (licenceType && licenceType.value) {
          return field.value.getTime() < new Date().getTime
            ? "End date must be greater than today"
            : true;
        }
        return field.value.getTime() - startDateValue.getTime() <= 0
          ? "End Date must be greater than Start Date"
          : true;
      },
    },
  ];

  getName(
    customerId: string,
    licences: Licence[],
    customers: Customer[],
  ): string {
    const customer = customers.find(o => o.id === customerId);
    if (!customer) {
      throw new Error("Can't find customer");
    }
    let maxCount = 0;
    licences
      .filter(l => l.customer_id === customerId)
      .forEach(licence => {
        const nameExp = new RegExp(`^(?:${customer!.name})(?: (\\d+))?$`);
        const matches = nameExp.exec(licence.name || "");
        if (matches && matches.length === 2) {
          const currentCount = matches[1];
          if (currentCount) {
            maxCount = Math.max(maxCount, Number(currentCount));
          } else {
            maxCount = Math.max(1, maxCount);
          }
        }
      });
    return maxCount === 0 ? customer.name : `${customer.name} ${maxCount + 1}`;
  }

  buildData(
    fields: InputField[],
    licences: Licence[],
    customers: Customer[],
  ): LicenceInput {
    const getValue = (name: string, valueField?: string): any => {
      const field = fields.find(f => f.name === name);
      if (!field) {
        throw new Error(`Can't find field named '${name}'`);
      }
      return valueField ? field[valueField as keyof InputField] : field.value;
    };
    let customerInfo = {};
    const customerId: string = getValue("customer_id");
    if (customerId === "__addnew__") {
      const name = getValue("customer_id", "enumNewValue");
      customerInfo = {
        new_customer: {
          name,
          email: getValue("customer_email"),
        },
        name,
      };
    } else {
      customerInfo = {
        name: this.getName(customerId, licences, customers),
        customer_id: customerId,
      };
    }
    const licenceType = getValue("type") ? "floating" : "fixed";
    return {
      ...customerInfo,
      tag: getValue("tag"),
      description: getValue("description"),
      installation_id: getValue("installation_id"),
      migrations: getValue("migrations"),
      replicas: getValue("replicas"),
      version: normalizeLicenceVersion(getValue("version")),
      type: licenceType,
      start_date: licenceType === "floating" ? null : getValue("start_date"),
      duration: licenceType === "fixed" ? null : getValue("duration"),
      end_date: getValue("end_date"),
      support: getValue("support") || null,
    };
  }
}

export default new InputFields();
